import { Checkmark, Menu } from '@carbon/icons-react';
import { Breadcrumb, Button, Drawer } from 'antd';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom';
import classNames from 'classnames';
import { FAMILY_APPLICATION_STEPS } from '../familyApplicationConstants';
import {
  doesChildCareNeedsStepApply,
  doesCareNeedTypeStepApply,
  doesScheduleOfCareStepApply,
  doesDailyTimeBlocksStepApply,
  doesProviderInfoStepApply,
  isChildStepCompleted,
  requiresAdditionalParent,
} from '../atoms';
import { getFullName } from 'utils';

// Styles
import './FamilyApplicationNavigation.less';
import { ADULT_MARITAL_STATUS_OPTIONS } from '../__steps/household/constants';

export const FamilyApplicationNavigation = ({ application, stepConfig, baseRoute }) => {
  const history = useHistory();
  const location = useLocation();

  // Build dynamic children sections
  const buildChildrenSection = () => {
    const children = application?.children || [];
    const childrenSteps = [];

    // Add steps for each existing child
    children.forEach((child, index) => {
      const childDisplayName = getFullName(child);

      // Add child name as a header step if basic info is filled
      if (childDisplayName) {
        childrenSteps.push({
          title: childDisplayName,
          stepId: `CHILD_${index}_HEADER`,
          isChildHeader: true,
          childIndex: index,
        });
      }

      // Add the 4 steps for this child
      childrenSteps.push(
        {
          title: 'Basic Info',
          urlPattern: `/children-basic-info?entity=${index}`,
          stepId: FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO,
          childIndex: index,
          isChildStep: true,
        },
        {
          title: 'Living Situation',
          urlPattern: `/children-living-situation?entity=${index}`,
          stepId: FAMILY_APPLICATION_STEPS.CHILDREN_LIVING_SITUATION,
          childIndex: index,
          isChildStep: true,
        },
        {
          title: 'Educational Status',
          urlPattern: `/children-educational-status?entity=${index}`,
          stepId: FAMILY_APPLICATION_STEPS.CHILDREN_EDUCATIONAL_STATUS,
          childIndex: index,
          isChildStep: true,
        },
        {
          title: 'Provider Connection',
          urlPattern: `/children-provider-connection?entity=${index}`,
          stepId: FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_CONNECTION,
          childIndex: index,
          isChildStep: true,
        },
        // Only show Provider Information step when provider connection indicates they have a provider
        ...(doesProviderInfoStepApply(child)
          ? [
              {
                title: 'Provider Information',
                urlPattern: `/children-provider-info?entity=${index}`,
                stepId: FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO,
                childIndex: index,
                isChildStep: true,
              },
            ]
          : []),
        ...(doesChildCareNeedsStepApply(child)
          ? [
              {
                title: 'Child Care Needs',
                urlPattern: `/children-child-care-needs?entity=${index}`,
                stepId: FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS,
                childIndex: index,
                isChildStep: true,
              },
            ]
          : []),
        ...(doesCareNeedTypeStepApply(child)
          ? [
              {
                title: 'Care Need Type',
                urlPattern: `/children-care-need-type?entity=${index}`,
                stepId: FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE,
                childIndex: index,
                isChildStep: true,
              },
            ]
          : []),
        ...(doesScheduleOfCareStepApply(child)
          ? [
              {
                title: 'Schedule of Care',
                urlPattern: `/children-schedule-of-care?entity=${index}`,
                stepId: FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE,
                childIndex: index,
                isChildStep: true,
              },
            ]
          : []),
        ...(doesDailyTimeBlocksStepApply(child)
          ? [
              {
                title: 'Daily Time Blocks',
                urlPattern: `/children-daily-time-blocks?entity=${index}`,
                stepId: FAMILY_APPLICATION_STEPS.CHILDREN_DAILY_TIME_BLOCKS,
                childIndex: index,
                isChildStep: true,
              },
            ]
          : []),
      );
    });

    // Add basic info step for the next child if current children are completed
    if (children.length === 0) {
      // No children yet, show first child's basic info
      childrenSteps.push({
        title: 'Basic Info',
        urlPattern: '/children-basic-info?entity=0',
        stepId: FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO,
        childIndex: 0,
        isChildStep: true,
      });
    } else {
      // Check if we should show the next child's basic info step
      const lastChild = children[children.length - 1];
      const isLastChildCompleted =
        lastChild?.firstName &&
        lastChild?.lastName &&
        lastChild?.dateOfBirth &&
        lastChild?.relationToApplicant &&
        lastChild?.applicantOnBirthCertificate !== undefined &&
        lastChild?.livingSituation &&
        lastChild?.educationalStatus &&
        lastChild?.childCareNeeds &&
        lastChild?.providerSituation;

      if (isLastChildCompleted) {
        const nextChildIndex = children.length;
        childrenSteps.push({
          title: 'Basic Info',
          urlPattern: `/children-basic-info?entity=${nextChildIndex}`,
          stepId: FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO,
          childIndex: nextChildIndex,
          isChildStep: true,
        });
      }
    }

    // Add summary step if there are children
    if (children.length > 0) {
      childrenSteps.push({
        title: 'Children Summary',
        urlPattern: '/children-summary',
        stepId: FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY,
      });

      // Add child eligibility certification step
      childrenSteps.push({
        title: 'Child Eligibility Certification',
        urlPattern: '/children-eligibility-certification',
        stepId: FAMILY_APPLICATION_STEPS.CHILDREN_ELIGIBILITY_CERTIFICATION,
      });

      // Add children documents step
      childrenSteps.push({
        title: "Children's Documents",
        urlPattern: '/children-documents',
        stepId: FAMILY_APPLICATION_STEPS.CHILDREN_DOCUMENTS,
      });
    }

    return {
      name: 'Children & Care',
      steps: childrenSteps,
    };
  };

  const _requiresAdditionalParent = requiresAdditionalParent(application);

  const sections = [
    {
      name: application?.applicant?.firstName ? getFullName(application?.applicant) : 'Service Member',
      steps: [
        {
          title: 'Basic Info',
          urlPattern: '/start',
          stepId: FAMILY_APPLICATION_STEPS.START,
        },
        {
          title: 'Marital Status',
          urlPattern: '/marital-status',
          stepId: FAMILY_APPLICATION_STEPS.MARITAL_STATUS,
        },
        {
          title: 'Contact Information',
          urlPattern: '/contact',
          stepId: FAMILY_APPLICATION_STEPS.CONTACT_INFORMATION,
        },
        {
          title: 'Location Information',
          urlPattern: '/location',
          stepId: FAMILY_APPLICATION_STEPS.LOCATION_INFORMATION,
        },
        {
          title: 'Component',
          urlPattern: '/component',
          stepId: FAMILY_APPLICATION_STEPS.COMPONENT,
        },
        {
          title: 'Status',
          urlPattern: '/status',
          stepId: FAMILY_APPLICATION_STEPS.STATUS,
        },
        {
          title: 'Rank',
          urlPattern: '/rank',
          stepId: FAMILY_APPLICATION_STEPS.RANK,
        },
        {
          title: 'Civilian Employment',
          urlPattern: '/civilian-employment',
          stepId: FAMILY_APPLICATION_STEPS.CIVILIAN_EMPLOYMENT,
        },
        {
          title: 'Military Documents',
          urlPattern: '/documents',
          stepId: FAMILY_APPLICATION_STEPS.DOCUMENTS,
        },
      ],
    },
    ...(_requiresAdditionalParent
      ? [
          {
            name: application?.additionalParents?.[0]?.firstName
              ? getFullName(application?.additionalParents?.[0])
              : application?.applicant?.maritalStatus === ADULT_MARITAL_STATUS_OPTIONS.MARRIED
              ? 'Spouse'
              : ' Other Adult',
            steps: [
              {
                title: 'Basic Info',
                urlPattern: '/household-structure',
                stepId: FAMILY_APPLICATION_STEPS.HOUSEHOLD_STRUCTURE,
              },
              {
                title: 'Contact Info',
                urlPattern: '/spouse-contact-info',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_CONTACT_INFO,
              },
              {
                title: 'Residence Status',
                urlPattern: '/spouse-residence-status',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_RESIDENCE_STATUS,
              },
              {
                title: 'Employment Activities',
                urlPattern: '/employment-activities',
                stepId: FAMILY_APPLICATION_STEPS.EMPLOYMENT_ACTIVITIES,
              },
              {
                title: 'Employment Type',
                urlPattern: '/spouse-employment-type',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYMENT_TYPE,
              },
              {
                title: 'Employer Details',
                urlPattern: '/spouse-employer-details',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYER_DETAILS,
              },
              {
                title: 'Self-Employment',
                urlPattern: '/spouse-self-employment',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_SELF_EMPLOYMENT,
              },
              {
                title: 'Military Employment',
                urlPattern: '/spouse-military-employment',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_MILITARY_EMPLOYMENT,
              },
              {
                title: 'Education',
                urlPattern: '/spouse-education',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_EDUCATION,
              },
              {
                title: 'Eligibility Summary',
                urlPattern: '/eligibility-summary',
                stepId: FAMILY_APPLICATION_STEPS.ELIGIBILITY_SUMMARY,
              },
              {
                title: 'Current Circumstances',
                urlPattern: '/spouse-unemployment-reason',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_UNEMPLOYMENT_REASON,
              },
              {
                title: 'Looking for Work Certification',
                urlPattern: '/spouse-job-search',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_JOB_SEARCH,
              },
              {
                title: 'Documents',
                urlPattern: '/spouse-documentation-requirements',
                stepId: FAMILY_APPLICATION_STEPS.SPOUSE_DOCUMENTATION_REQUIREMENTS,
              },
            ],
          },
        ]
      : []),
    buildChildrenSection(),
    {
      name: 'Documentation',
      steps: [
        {
          title: 'Document Checklist',
          urlPattern: '/documentation-requirements',
          stepId: FAMILY_APPLICATION_STEPS.DOCUMENTATION_REQUIREMENTS,
        },
      ],
    },
    {
      name: 'Agreement',
      steps: [
        {
          title: 'Agreement',
          urlPattern: '/agreement',
          stepId: FAMILY_APPLICATION_STEPS.AGREEMENT,
        },
      ],
    },
    {
      name: 'Submitted',
      steps: [
        {
          title: 'Submitted',
          urlPattern: '/submitted',
          stepId: FAMILY_APPLICATION_STEPS.SUBMITTED,
        },
      ],
    },
  ];

  const currentSectionIndex = sections.findIndex((section) =>
    section.steps.some((step) => {
      // For child steps with query parameters, match both pathname and query exactly
      if (step.urlPattern?.includes('?entity=')) {
        const [pathname, query] = step.urlPattern.split('?');

        return location.pathname.endsWith(pathname) && location.search === `?${query}`;
      }
      // For regular steps, use endsWith for more precise matching
      return location.pathname.endsWith(step.urlPattern);
    }),
  );
  const currentSection = sections[currentSectionIndex];
  const currentStepIndex = sections[currentSectionIndex]?.steps?.findIndex((step) => {
    // Use the same precise matching logic
    if (step.urlPattern?.includes('?entity=')) {
      const [pathname, query] = step.urlPattern?.split('?');
      return location.pathname.endsWith(pathname) && location.search === `?${query}`;
    }

    return location.pathname.endsWith(step.urlPattern);
  });
  const currentStep = sections[currentSectionIndex]?.steps?.[currentStepIndex];

  // Get better breadcrumb information for child steps
  const getBreadcrumbInfo = () => {
    if (!currentStep || !currentStep.isChildStep) {
      return {
        sectionTitle: `${currentSectionIndex + 1}. ${currentSection?.name}`,
        stepTitle: currentStep?.title,
      };
    }

    // For child steps, show child name if available
    const children = application?.children || [];
    const child = children[currentStep.childIndex];
    const childDisplayName = getFullName(child);

    return {
      sectionTitle: `${currentSectionIndex + 1}. ${currentSection?.name}`,
      stepTitle: `${childDisplayName} - ${currentStep.title}`,
    };
  };

  const breadcrumbInfo = getBreadcrumbInfo();

  // Custom color palette
  const colors = {
    primary: '#4338ca', // Indigo-700
    lightBg: '#eef2ff', // Indigo-50
    gray: '#d1d5db', // Gray-300
    success: 'oklch(52.7% 0.154 150.069)', // Green-600
  };

  const [drawerOpen, setDrawerOpen] = useState(false);

  const isStepDisabled = (stepId, entityIndex = null) => {
    if (!stepConfig || !stepConfig[stepId]) return false;
    return typeof stepConfig[stepId].disabled === 'function'
      ? stepConfig[stepId].disabled(application, stepConfig, entityIndex)
      : stepConfig[stepId].disabled;
  };

  const isStepCompleted = (stepId) => {
    if (!stepConfig || !stepConfig[stepId]) return false;
    return stepConfig[stepId].isCompleted?.(application) || false;
  };

  const isStepApplicable = (stepId) => {
    if (!stepConfig || !stepConfig[stepId]) {
      console.error('isStepApplicable => stepConfig is null or stepConfig[stepId] is undefined', stepId);
      return true;
    }

    return typeof stepConfig[stepId].doesApply === 'function'
      ? stepConfig[stepId].doesApply(application)
      : stepConfig[stepId].doesApply;
  };

  const isChildStepApplicable = (step) => {
    const childIndex = step.childIndex;
    const children = application?.children || [];

    // If this is not the first child and there are no children yet, don't show
    if (childIndex > 0 && children.length <= childIndex) {
      return false;
    }

    // If this child doesn't exist and it's not child 0, don't show
    if (childIndex > 0 && !children[childIndex]) {
      return false;
    }

    // For child steps beyond basic info, check if basic info is completed
    if (step.stepId !== FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO) {
      const child = children[childIndex];
      if (!child?.firstName || !child?.lastName || !child?.dateOfBirth) {
        return false; // Don't show if basic info not completed
      }
    }

    // Check step config's doesApply function with entity index
    if (stepConfig && stepConfig[step.stepId]) {
      const doesApply = stepConfig[step.stepId].doesApply;
      if (typeof doesApply === 'function') {
        if (!doesApply(application, childIndex)) {
          return false;
        }
      } else if (doesApply === false) {
        return false;
      }
    }

    return true;
  };

  const isSectionAvailable = (section) => {
    return section.steps.some((step) => {
      if (step.isChildHeader) return false;

      const isApplicable = step.isChildStep ? isChildStepApplicable(step) : isStepApplicable(step.stepId);
      if (!isApplicable) return false;

      const isDisabled = isStepDisabled(step.stepId, step.childIndex);
      return !isDisabled;
    });
  };

  // Don't render navigation if stepConfig is not loaded yet
  if (!stepConfig) {
    return null;
  }

  return (
    <div className="family-application-navigation">
      <div
        className="bg-[rgb(12,18,67)] hover:bg-[rgb(10,15,54)] cursor-pointer px-7 py-4 text-white flex items-center text-lg gap-4 transition-all duration-100"
        onClick={() => {
          setDrawerOpen(true);
        }}
      >
        <Button icon={<Menu className="w-7 h-7 mb-[-1px]" />} className="border-black">
          View all steps
        </Button>

        {!!breadcrumbInfo?.sectionTitle && !!breadcrumbInfo?.stepTitle && (
          <Breadcrumb
            items={[
              {
                title: breadcrumbInfo.sectionTitle,
                className: 'text-white',
              },
              {
                title: breadcrumbInfo.stepTitle,
                className: 'text-white font-semibold',
              },
            ]}
          />
        )}
      </div>

      {/* Navigation drawer - slides in from left */}
      <Drawer title="Application Steps" open={drawerOpen} placement="left" onClose={() => setDrawerOpen(false)}>
        <div className="flex-1 overflow-y-auto text-lg mb-16">
          {sections.map((section, sectionIndex) => {
            const sectionAvailable = isSectionAvailable(section);
            return (
              <div key={sectionIndex} className="mb-4">
                <div
                  className={classNames('p-2 px-4 mb-1 font-semibold rounded', {
                    'font-medium': sectionIndex === currentSectionIndex,
                    'mt-8': sectionIndex > 0,
                    'cursor-pointer': sectionAvailable,
                    'cursor-not-allowed opacity-50': !sectionAvailable,
                  })}
                  onClick={() => {
                    if (!sectionAvailable) return;

                    // Find the first step that is both applicable and not disabled
                    const firstAvailableStep = section.steps.find((step) => {
                      if (step.isChildHeader) return false;

                      const isApplicable = step.isChildStep
                        ? isChildStepApplicable(step)
                        : isStepApplicable(step.stepId);
                      if (!isApplicable) return false;

                      const isDisabled = isStepDisabled(step.stepId, step.childIndex);
                      return !isDisabled;
                    });

                    if (firstAvailableStep) {
                      history.push(`${baseRoute}/${application?.id}${firstAvailableStep.urlPattern}`);
                      setDrawerOpen(false);
                    }
                  }}
                  style={{
                    backgroundColor: sectionIndex === currentSectionIndex ? colors.lightBg : 'transparent',
                    color:
                      sectionIndex === currentSectionIndex
                        ? colors.primary
                        : sectionAvailable
                        ? 'rgba(55, 65, 81, 0.8)'
                        : 'rgba(55, 65, 81, 0.4)',
                  }}
                >
                  {sectionIndex + 1}. {section.name}
                </div>

                <div className="ml-4 pl-2" style={{ borderLeft: `1px solid ${colors.gray}` }}>
                  {section.steps.map((step, stepIndex) => {
                    const isCurrent = sectionIndex === currentSectionIndex && stepIndex === currentStepIndex;

                    // Handle child header steps (these are not clickable, just labels)
                    if (step.isChildHeader) {
                      // Check if the first step of this child (Basic Info) is disabled
                      const isChildDisabled = isStepDisabled(
                        FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO,
                        step.childIndex,
                      );

                      return (
                        <div
                          key={stepIndex}
                          className={`p-2 pb-1 text-[14px] mb-2 font-semibold border-solid border-b border-b-solid border-gray-300 ${
                            isChildDisabled ? 'text-gray-400' : 'text-gray-700'
                          }`}
                        >
                          {step.title}
                        </div>
                      );
                    }

                    const isApplicable = step.isChildStep ? isChildStepApplicable(step) : isStepApplicable(step.stepId);
                    if (!isApplicable) return null;

                    const isDisabled = isStepDisabled(step.stepId, step.childIndex);
                    const isCompleted = step.isChildStep
                      ? isChildStepCompleted(step.stepId, application?.children?.[step.childIndex])
                      : isStepCompleted(step.stepId);

                    return (
                      <button
                        key={stepIndex}
                        onClick={() => {
                          if (!isDisabled) {
                            history.push(`${baseRoute}/${application?.id}${step.urlPattern}`);
                            setDrawerOpen(false);
                          }
                        }}
                        className={classNames(
                          'w-full text-left p-2 rounded text-[14px] mb-1 flex items-center gap-1 transition-all duration-75',
                          {
                            'hover:bg-[oklch(0.7_0.2_149.17_/_0.1)]': !isCurrent && isCompleted && !isDisabled,
                            'hover:bg-gray-100': !isCurrent && !isCompleted && !isDisabled,
                            'cursor-pointer': !isDisabled,
                            'cursor-not-allowed opacity-50': isDisabled,
                          },
                          {
                            // Background classes
                            'bg-[#4338ca]': isCurrent, // Using exact primary color
                            'bg-transparent': !isCurrent,

                            // Text color classes
                            'text-white': isCurrent,
                            'text-[oklch(52.7%_0.154_150.069)]': !isCurrent && isCompleted && !isDisabled, // Completed steps - green
                            'text-[rgba(55,65,81,0.8)]': !isCurrent && (!isCompleted || isDisabled), // Incomplete/disabled steps - gray
                          },
                          // Add indentation for child steps
                          step.isChildStep ? 'ml-4' : '',
                        )}
                        disabled={isDisabled}
                      >
                        {isCompleted && !isDisabled && (
                          <Checkmark
                            size={14}
                            className="inline mr-1"
                            style={{
                              color:
                                sectionIndex === currentSectionIndex && stepIndex === currentStepIndex
                                  ? 'white'
                                  : colors.success,
                            }}
                          />
                        )}

                        {step.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Drawer>
    </div>
  );
};
